// How wide a piece of text will draw, for laying out labels in an svg. A canvas measures it with the real font; where there is no canvas (a unit test, a very old browser) it falls back to an
// estimate from the character count, so layout never throws and never returns NaN.
const cache = new Map<string, number>();
let context: CanvasRenderingContext2D | null | undefined;

const canvasContext = (): CanvasRenderingContext2D | null => {
  if (context !== undefined) return context;
  try {
    context = typeof document !== "undefined" ? document.createElement("canvas").getContext("2d") : null;
  } catch (e) {
    context = null;
  }

  return context;
};

/** the average width of one character as a share of the font size (Fira Sans / sans-serif, mixed text): only used when nothing can measure */
export const ESTIMATED_CHAR_EM = 0.56;

export const estimateTextWidth = (text: string, px: number): number => text.length * px * ESTIMATED_CHAR_EM;

/** the drawn width in px of `text` in `px`-size `family` at `weight`; cached, because labels are measured on every layout pass */
export const textWidth = (text: string, px: number, weight: number | string = 400, family = "Fira Sans, sans-serif"): number => {
  if (!text) return 0;
  const key = `${weight}|${px}|${family}|${text}`;
  const known = cache.get(key);
  if (known !== undefined) return known;
  const ctx = canvasContext();
  let width = estimateTextWidth(text, px);
  if (ctx) {
    ctx.font = `${weight} ${px}px ${family}`;
    const measured = ctx.measureText(text).width;
    if (Number.isFinite(measured) && measured > 0) width = measured;
  }
  if (cache.size > 2000) cache.clear(); // names are many (every referrer, every city): never grow without limit
  cache.set(key, width);

  return width;
};
